<app-page-shell>
    <p-toast position="bottom-center"></p-toast>
    <p-progressBar mode="indeterminate" class="m-2" *ngIf="isLoading"></p-progressBar>

    <app-page-hero eyebrow="Ledgers" title="General Ledger"
        subtitle="Audit posted, balanced journal lines with backend-owned running balances and source trace.">
        <div actions class="ledger-actions">
            <p-button label="Generate" icon="pi pi-refresh" (onClick)="applyFilters()"></p-button>
            <p-button label="Reset" icon="pi pi-filter-slash" severity="secondary" [outlined]="true"
                (onClick)="clearFilters()"></p-button>
            <p-button label="Export" icon="pi pi-file-excel" severity="success" (onClick)="exportExcel()"></p-button>
            <p-button label="Print" icon="pi pi-print" severity="secondary" [outlined]="true"
                (onClick)="printReport()"></p-button>
        </div>
    </app-page-hero>

    <app-page-metric-strip [metrics]="metrics"></app-page-metric-strip>

    <app-page-data-panel *ngIf="diagnostics.length > 0" title="Report Diagnostics"
        [resultText]="diagnostics.length + ' issue(s)'">
        <div class="diagnostic-list">
            <div *ngFor="let diagnostic of diagnostics" class="diagnostic-item">
                <div class="diagnostic-title">
                    <span>{{ diagnostic.code }}</span>
                    <p-tag [value]="diagnostic.severity" [severity]="diagnosticSeverity(diagnostic.severity)"></p-tag>
                </div>
                <div>{{ diagnostic.message }}</div>
                <small *ngIf="diagnostic.referenceNumber">Reference: {{ diagnostic.referenceNumber }}</small>
            </div>
        </div>
    </app-page-data-panel>

    <app-page-form-section icon="pi pi-filter" title="Ledger Filters"
        subtitle="Use a compact accounting filter set; advanced criteria stay available without widening the report.">
        <div class="ledger-filter-grid">
            <p-iftalabel>
                <p-datePicker inputId="filterTransactionDate" [(ngModel)]="filterDateRange" selectionMode="range"
                    [readonlyInput]="true" dateFormat="yy-mm-dd" [showIcon]="true" styleClass="w-full"></p-datePicker>
                <label for="filterTransactionDate">Transaction Date Range</label>
            </p-iftalabel>

            <p-iftalabel>
                <p-treeSelect inputId="filterAccounts" class="w-full" [(ngModel)]="selectedAccounts"
                    [options]="chartOfAccountsTree" selectionMode="checkbox" [filter]="true"
                    filterPlaceholder="Search Accounts"></p-treeSelect>
                <label for="filterAccounts">Accounts</label>
            </p-iftalabel>

            <p-iftalabel>
                <input id="filterDescription" type="text" pInputText [(ngModel)]="filterDescription" />
                <label for="filterDescription">Quick Search / Description</label>
            </p-iftalabel>

            <div class="filter-command-bar">
                <p-button label="Apply" icon="pi pi-filter" (onClick)="applyFilters()"></p-button>
                <p-button icon="pi pi-filter-slash" severity="secondary" [outlined]="true" pTooltip="Clear filters"
                    (onClick)="clearFilters()"></p-button>
            </div>
        </div>

        <details class="ledger-advanced-filters">
            <summary>
                <i class="pi pi-sliders-h"></i>
                Advanced filters
            </summary>
            <div class="ledger-filter-grid advanced">
                <p-iftalabel>
                    <p-multiSelect inputId="filterJournals" [(ngModel)]="filterJournals" [options]="journals"
                        optionLabel="journalName" display="chip" styleClass="w-full"></p-multiSelect>
                    <label for="filterJournals">Journals</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="filterPartnerTypes" [(ngModel)]="filterPartyTypes" [options]="partyTypes"
                        display="chip" (onChange)="onPartnerTypeChange()" styleClass="w-full"></p-multiSelect>
                    <label for="filterPartnerTypes">Partner Types</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="filterPartnerNames" [(ngModel)]="selectedParties"
                        [options]="partnerNameOptions" optionLabel="entityName" [filter]="true" filterBy="entityName"
                        display="chip" [disabled]="!filterPartyTypes || filterPartyTypes.length === 0"
                        styleClass="w-full"></p-multiSelect>
                    <label for="filterPartnerNames">Partner Names</label>
                </p-iftalabel>

                <p-iftalabel>
                    <input id="filterTransactionNumber" type="text" pInputText
                        [(ngModel)]="filterTransactionNumber" />
                    <label for="filterTransactionNumber">Transaction #</label>
                </p-iftalabel>

                <p-iftalabel>
                    <input id="filterReference" type="text" pInputText [(ngModel)]="filterReference" />
                    <label for="filterReference">Reference</label>
                </p-iftalabel>

                <p-iftalabel>
                    <app-logistics-project-lookup inputId="filterLogisticsProject" [(ngModel)]="filterLogisticsProjectId"></app-logistics-project-lookup>
                    <label for="filterLogisticsProject">Logistics Project</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="filterDimensionTypes" [(ngModel)]="filterDimensionTypes"
                        [options]="dimensionTypes" optionLabel="name" optionValue="dimensionTypeId" display="chip"
                        (onChange)="onDimensionTypeFilterChange()" styleClass="w-full"></p-multiSelect>
                    <label for="filterDimensionTypes">Dimension Types</label>
                </p-iftalabel>

                <p-iftalabel>
                    <p-multiSelect inputId="filterDimensionValues" [(ngModel)]="selectedDimensionValues"
                        [options]="dimensionValueOptions" optionLabel="name" optionValue="id" [filter]="true"
                        filterBy="name" display="chip"
                        [disabled]="!filterDimensionTypes || filterDimensionTypes.length === 0"
                        styleClass="w-full"></p-multiSelect>
                    <label for="filterDimensionValues">Dimension Values</label>
                </p-iftalabel>
            </div>
        </details>
    </app-page-form-section>

    <app-page-data-panel title="Ledger Entries"
        subtitle="Visible columns are grouped for review; expand a line for source, description, dimensions, and IDs."
        [resultText]="totalRecords + ' entries'">
        <div actions class="ledger-table-actions">
            <span class="currency-chip">{{ reportCurrencyCode }}</span>
        </div>

        <p-table [value]="allFilteredLedgerEntries" dataKey="journalItemId" responsiveLayout="scroll"
            styleClass="p-datatable-gridlines p-datatable-sm ledger-audit-table" [tableStyle]="{ 'min-width': '82rem' }"
            [scrollable]="true" scrollHeight="flex" [resizableColumns]="true" columnResizeMode="fit"
            [paginator]="true" [rows]="rows" [first]="first" [totalRecords]="totalRecords" [lazy]="true"
            (onLazyLoad)="loadLedgerEntries($event)" [showCurrentPageReport]="true"
            currentPageReportTemplate="Showing {first} to {last} of {totalRecords} entries"
            [rowsPerPageOptions]="[25, 50, 100, 200]" [sortField]="'transactionDate'" [sortOrder]="-1"
            [multiSortMeta]="[{field: 'transactionDate', order: -1}, {field: 'jeEntryId', order: 1}]"
            sortMode="multiple">
            <ng-template pTemplate="header">
                <tr>
                    <th pResizableColumn pSortableColumn="transactionDate" class="date-col">
                        Date <p-sortIcon field="transactionDate"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="accountCode">
                        Account <p-sortIcon field="accountCode"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="transactionNumber">
                        Document <p-sortIcon field="transactionNumber"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="partnerName">
                        Party <p-sortIcon field="partnerName"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="debit" class="numeric amount-col">
                        Debit <p-sortIcon field="debit"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="credit" class="numeric amount-col">
                        Credit <p-sortIcon field="credit"></p-sortIcon>
                    </th>
                    <th pResizableColumn pSortableColumn="balance" class="numeric amount-col">
                        Balance <p-sortIcon field="balance"></p-sortIcon>
                    </th>
                    <th class="actions-col">Actions</th>
                </tr>
            </ng-template>

            <ng-template pTemplate="body" let-ledger let-expanded="expanded">
                <tr>
                    <td>
                        <div class="cell-stack">
                            <strong>{{ ledger.transactionDate | date:'yyyy-MM-dd' }}</strong>
                            <span class="cell-meta">{{ ledger.state || 'Posted' }}</span>
                        </div>
                    </td>
                    <td>
                        <div class="cell-stack">
                            <strong>{{ ledger.accountCode || '-' }}</strong>
                            <span>{{ ledger.accountName || '-' }}</span>
                            <p-tag *ngIf="ledger.internalType" [value]="ledger.internalType"
                                severity="secondary"></p-tag>
                        </div>
                    </td>
                    <td>
                        <div class="cell-stack">
                            <strong>{{ ledger.transactionNumber || ledger.sourceDocumentNumber || '-' }}</strong>
                            <span class="cell-meta">
                                <i class="pi pi-book"></i>
                                {{ ledger.journal || '-' }}
                            </span>
                            <span class="cell-meta" *ngIf="ledger.reference">
                                <i class="pi pi-bookmark"></i>
                                {{ ledger.reference }}
                            </span>
                        </div>
                    </td>
                    <td>
                        <div class="cell-stack">
                            <strong>{{ ledger.partnerName || '-' }}</strong>
                            <p-tag *ngIf="ledger.partnerType" [value]="ledger.partnerType" severity="info"></p-tag>
                        </div>
                    </td>
                    <td class="numeric amount debit">
                        {{ ledger.debit | currency:rowCurrency(ledger):'symbol':'1.2-2' }}
                    </td>
                    <td class="numeric amount credit">
                        {{ ledger.credit | currency:rowCurrency(ledger):'symbol':'1.2-2' }}
                    </td>
                    <td class="numeric amount balance">
                        {{ ledger.balance | currency:rowCurrency(ledger):'symbol':'1.2-2' }}
                    </td>
                    <td class="actions-col">
                        <p-button [icon]="expanded ? 'pi pi-chevron-down' : 'pi pi-chevron-right'" [text]="true"
                            [rounded]="true" severity="secondary" [pRowToggler]="ledger"
                            pTooltip="Toggle audit details"></p-button>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="expandedrow" let-ledger>
                <tr>
                    <td colspan="8" class="ledger-row-expansion">
                        <div class="detail-grid">
                            <div>
                                <span class="detail-label">Description</span>
                                <strong>{{ ledger.description || '-' }}</strong>
                            </div>
                            <div>
                                <span class="detail-label">Source</span>
                                <strong>{{ sourceLabel(ledger) }}</strong>
                            </div>
                            <div>
                                <span class="detail-label">Journal Entry ID</span>
                                <strong>{{ ledger.jeEntryId || '-' }}</strong>
                            </div>
                            <div>
                                <span class="detail-label">Journal Line ID</span>
                                <strong>{{ ledger.journalItemId || '-' }}</strong>
                            </div>
                            <div>
                                <span class="detail-label">Reference</span>
                                <strong>{{ ledger.reference || '-' }}</strong>
                            </div>
                            <div>
                                <span class="detail-label">Partner</span>
                                <strong>{{ ledger.partnerName || '-' }}</strong>
                            </div>
                        </div>
                        <div class="dimension-strip" *ngIf="ledger.dimensions?.length; else noDimensions">
                            <p-chip *ngFor="let dimension of ledger.dimensions"
                                [label]="dimensionLabel(dimension)"></p-chip>
                        </div>
                        <ng-template #noDimensions>
                            <div class="empty-detail">No line dimensions recorded.</div>
                        </ng-template>
                    </td>
                </tr>
            </ng-template>

            <ng-template pTemplate="footer" *ngIf="allFilteredLedgerEntries.length > 0">
                <tr class="ledger-total-row">
                    <td colspan="4" class="numeric font-bold">Totals for filtered period</td>
                    <td class="numeric font-bold debit">
                        {{ formatMoney(totalFilteredDebits) }}
                    </td>
                    <td class="numeric font-bold credit">
                        {{ formatMoney(totalFilteredCredits) }}
                    </td>
                    <td class="numeric font-bold balance">
                        {{ formatBalanceSides(closingBalance, closingDebitBalance, closingCreditBalance) }}
                    </td>
                    <td></td>
                </tr>
            </ng-template>

            <ng-template pTemplate="emptymessage">
                <tr>
                    <td colspan="8" class="empty-cell">No ledger entries found for the selected criteria.</td>
                </tr>
            </ng-template>
        </p-table>
    </app-page-data-panel>
</app-page-shell>
